/**
 * `docs/DESIGN.md`, "Solo": "sprint — clear a configurable number of lines,
 * timed; or, in the timed variant, clear as many lines as possible before a
 * time limit." The owner amendment's "Mode configuration" table is the goal
 * axis (10/20/40(default)/100 lines, OR a 1/2/3/5 minute time limit) and the
 * hardness axis (both apply here; hardness only ever touches the gravity
 * speed for sprint — there is no cadence for it to also change).
 *
 * `buildSprintMode` replaces the old static `sprintMode` export: the
 * descriptor is now a function of a `ModeConfig`, not a fixed value, because
 * the goal text, the win/lose predicate and the HUD's "lines" readout all
 * depend on which goal was chosen. Everything the config touches is
 * computed once, in this closure, and baked into the returned descriptor —
 * `GameScreen.svelte` never branches on the goal shape itself.
 */
import { formatClock } from './format.js';
import { HARDNESS_GRAVITY_PERMILLE, SPRINT_DEFAULT_LINE_GOAL } from './config.js';
import type { ModeConfig, SprintGoal } from './config.js';
import type { ModeDescriptor } from './types.js';

function goalText(goal: SprintGoal): string {
  return goal.kind === 'lines'
    ? `Clear ${String(goal.lines)} lines as fast as you can.`
    : `Clear as many lines as you can in ${String(goal.ms / 60_000)} minute${goal.ms === 60_000 ? '' : 's'}.`;
}

export function buildSprintMode(config: ModeConfig): ModeDescriptor {
  const goal: SprintGoal = config.goal ?? { kind: 'lines', lines: SPRINT_DEFAULT_LINE_GOAL };
  const speedPermille = HARDNESS_GRAVITY_PERMILLE[config.hardness];

  return {
    id: 'sprint',
    copy: {
      name: 'Sprint',
      tagline: 'Clear lines against a target, or the clock.',
      goal: goalText(goal),
    },
    engineOptions: { gravity: { speedPermille } },

    onTick(_state, runtime, tickMs) {
      runtime.elapsedMs += tickMs;
    },

    checkEnd(state, runtime) {
      if (state.topOut) {
        return {
          result: 'lose',
          title: 'Topped out',
          summary: `${String(state.lines)} lines in ${formatClock(runtime.elapsedMs)}.`,
        };
      }
      if (goal.kind === 'lines') {
        if (state.lines >= goal.lines) {
          return {
            result: 'win',
            title: `${String(goal.lines)} lines`,
            summary: `Done in ${formatClock(runtime.elapsedMs)}. That time goes on the board.`,
          };
        }
        return null;
      }
      // Timed variant: `docs/DESIGN.md` — "ending is always 'finished', result
      // = lines." There is no target to win or lose against once the clock
      // itself is the only thing that can end the run (topping out already
      // returned above).
      if (runtime.elapsedMs >= goal.ms) {
        return {
          result: 'finished',
          title: "Time's up",
          summary: `${String(state.lines)} lines cleared.`,
        };
      }
      return null;
    },

    hud:
      goal.kind === 'lines'
        ? [
            {
              id: 'lines',
              label: 'Lines',
              select: (state) =>
                `${String(Math.min(state.lines, goal.lines))}/${String(goal.lines)}`,
            },
            {
              id: 'time',
              label: 'Time',
              select: (_state, runtime) => formatClock(runtime.elapsedMs),
            },
          ]
        : [
            {
              id: 'lines',
              label: 'Lines',
              select: (state) => String(state.lines),
            },
            {
              id: 'time',
              label: 'Time left',
              select: (_state, runtime) => formatClock(Math.max(0, goal.ms - runtime.elapsedMs)),
            },
          ],

    score(state, runtime, outcome) {
      if (outcome.result === 'lose') return null; // topped out before finishing — nothing to compare
      if (goal.kind === 'lines') {
        return {
          value: runtime.elapsedMs,
          betterWhen: 'lower',
          formatted: formatClock(runtime.elapsedMs),
        };
      }
      return {
        value: state.lines,
        betterWhen: 'higher',
        formatted: `${String(state.lines)} lines`,
      };
    },
  };
}
